import API from '../api/api'

// MP Size Chart & Size Guide (Shopplaza/Avada) - backend routes /v2/api/partner/size_chart_avada/*
export const SIZE_CHART_SLUG = 'size_chart_avada';

const SYNC_SIZE_CHART_PENDING = 'SYNC_SIZE_CHART_PENDING';
const SYNC_SIZE_CHART_RESPONSE = 'SYNC_SIZE_CHART_RESPONSE';

const LOAD_SIZE_CHART_PENDING = 'LOAD_SIZE_CHART_PENDING';
const LOAD_SIZE_CHART_RESPONSE = 'LOAD_SIZE_CHART_RESPONSE';

const SAVE_SIZE_CHART_PENDING = 'SAVE_SIZE_CHART_PENDING';
const SAVE_SIZE_CHART_RESPONSE = 'SAVE_SIZE_CHART_RESPONSE';

const UPDATE_SIZE_CHART_PENDING = 'UPDATE_SIZE_CHART_PENDING';
const UPDATE_SIZE_CHART_RESPONSE = 'UPDATE_SIZE_CHART_RESPONSE';

const RESET_SIZE_CHART_ERRORS = 'RESET_SIZE_CHART_ERRORS';

const initialState = {
  sync_size_chart_pending: false,
  load_size_chart_pending: false,
  save_size_chart_pending: false,
  update_size_chart_pending: false,

  // [{id, shopifyId, key, label, fields: [{path, label, type, source, target}]}]
  metafields: [],
  // the sync's dry run: {count, empty, outdated, added, languages: [{id, code, name, count, empty,
  // outdated, added}]} - the stored translations the "update" button would rewrite, the still
  // empty translatable fields, the outdated translations and the texts added since the language
  // was last saved, per language; a finished update drops its language
  pendingUpdate: null,
  // 'free_plan' | 'no_metafields': the backend declined for a reason the page shows as an
  // empty state, not as an error
  unavailable: null,
  feedback: [],
  saveErrors: [],
  error: false,
};

const UNAVAILABLE_REASONS = ['free_plan', 'no_metafields'];
// a declined request names its reason; an unknown failure leaves the last known state alone
const unavailableFrom = (response, previous) => {
  if(response?.success === true) return null;
  if(response?.success === false) return UNAVAILABLE_REASONS.includes(response.reason) ? response.reason : null;
  return previous;
};
// anything but a confirmed success is an error: a 500, an HTML page or a dropped connection
// arrives as an Error object, not as {success: false}. `retry` re-dispatches the request
const errorFrom = (response, retry = null) => {
  if(response?.success === true) return false;
  if(response?.success === false && UNAVAILABLE_REASONS.includes(response.reason)) return false;
  return { message: response?.message || true, action: retry || undefined, level: 0 };
};

// REDUCER
export default (state = initialState, action) => {
  switch (action.type) {
    case SYNC_SIZE_CHART_PENDING:
      return { ...state, sync_size_chart_pending: true, error: false };
    case SYNC_SIZE_CHART_RESPONSE:
      return {
        ...state,
        sync_size_chart_pending: false,
        metafields: action.response?.success === true ? (action.response.data || []) : state.metafields,
        pendingUpdate: action.response?.success === true ? (action.response.pending_update || null) : state.pendingUpdate,
        unavailable: unavailableFrom(action.response, state.unavailable),
        error: errorFrom(action.response, action.retry),
      };

    case LOAD_SIZE_CHART_PENDING:
      return { ...state, load_size_chart_pending: true, error: false };
    case LOAD_SIZE_CHART_RESPONSE:
      return {
        ...state,
        load_size_chart_pending: false,
        metafields: action.response?.success === true ? (action.response.data || []) : state.metafields,
        unavailable: unavailableFrom(action.response, state.unavailable),
        error: errorFrom(action.response, action.retry),
      };

    case SAVE_SIZE_CHART_PENDING:
      return { ...state, save_size_chart_pending: true, saveErrors: [], error: false };
    case SAVE_SIZE_CHART_RESPONSE:
      return {
        ...state,
        save_size_chart_pending: false,
        metafields: action.response?.success === true ? (action.response.data || []) : state.metafields,
        feedback: action.response?.metafield_feedback || [],
        error: errorFrom(action.response),
      };

    case UPDATE_SIZE_CHART_PENDING:
      return { ...state, update_size_chart_pending: true, error: false };
    case UPDATE_SIZE_CHART_RESPONSE:
      {
        const doneLanguage = action.response?.success === true ? Number(action.response.language) : null;
        const languages = (state.pendingUpdate?.languages || []).filter(l => l.id !== doneLanguage);
        const sum = (key) => languages.reduce((n, l) => n + (l[key] || 0), 0);
        return {
          ...state,
          update_size_chart_pending: false,
          pendingUpdate: state.pendingUpdate ? { ...state.pendingUpdate, count: sum('count'), empty: sum('empty'), outdated: sum('outdated'), added: sum('added'), languages } : null,
          feedback: action.response?.metafield_feedback || [],
          // a failed language is reported inside the update dialog, not by the loading indicator
          error: false,
        };
      }

    case RESET_SIZE_CHART_ERRORS:
      return { ...state, error: false, saveErrors: [] };

    default:
      return state;
  }
};

// ACTION CREATORS
export const resetSizeChartErrors = () => (dispatch) =>
  dispatch({ type: RESET_SIZE_CHART_ERRORS });

export const syncSizeChartTexts = (languageId = '', callback = () => {}) => (dispatch) => {
  dispatch({ type: SYNC_SIZE_CHART_PENDING });
  API.syncPartnerTexts(SIZE_CHART_SLUG, languageId, (response) => {
    dispatch({ type: SYNC_SIZE_CHART_RESPONSE, response, retry: () => syncSizeChartTexts(languageId, callback) });
    callback(response);
  });
};

export const loadSizeChartTexts = (languageId) => (dispatch) => {
  dispatch({ type: LOAD_SIZE_CHART_PENDING });
  API.getPartnerTexts(SIZE_CHART_SLUG, languageId, (response) => {
    dispatch({ type: LOAD_SIZE_CHART_RESPONSE, response, retry: () => loadSizeChartTexts(languageId) });
  });
};

export const saveSizeChartTexts = (languageId, data, callback = () => {}) => (dispatch) => {
  dispatch({ type: SAVE_SIZE_CHART_PENDING });
  API.savePartnerTexts(SIZE_CHART_SLUG, languageId, data, (response) => {
    dispatch({ type: SAVE_SIZE_CHART_RESPONSE, response });
    callback(response);
  });
};

// one language per request; the section loops and reloads its view when the loop is through
export const updateSizeChartTexts = (languageId, callback = () => {}) => (dispatch) => {
  dispatch({ type: UPDATE_SIZE_CHART_PENDING });
  API.updatePartnerTexts(SIZE_CHART_SLUG, languageId, (response) => {
    dispatch({ type: UPDATE_SIZE_CHART_RESPONSE, response });
    callback(response);
  });
};
